import { memo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
  HalIncotermSelect,
  PSLSelect,
  ShipTermSelect,
  TransportationGroupSelect,
  UrgencySelect,
} from '@views/screens/common';
import { SHIPPING_TERMS_FIELDS } from '@views/screens/Quote/components/CreateGetQuote/Tabs/ShippingTerms/types';
import { Col, Form, Row } from 'antd';
import moment from 'moment';

import { FormSchema, useRegisterForm } from '@/modules/validators';
import { EquipmentTypeSelect } from '@/views/screens/Shipment';

import FormDatePicker from '../../../../../../components/Inputs/FormDatePicker';
import FormInput from '../../../../../../components/Inputs/FormInput';
import { transportationGroupData } from '../../../../constants';

const ShippingTermsSearchForm = () => {
  const [form] = Form.useForm();
  useRegisterForm(form, 'shippingTermsSearchForm');
  const shipDateValue = Form.useWatch('expShipDate', form);
  const location = useLocation();
  const prepopulatedData = location.state?.quote;

  const { data = {} } = useSelector((state) => state.shipment.shipmentDetails);
  const copyQuoteKey = useSelector((state) => state.common.copyQuoteKey);

  useEffect(() => {
    if (prepopulatedData) {
      form.setFieldsValue({
        expShipDate: prepopulatedData.shipDate ? moment(prepopulatedData.shipDate) : null,
      });
    }
  }, [form, prepopulatedData]);

  useEffect(() => {
    if (copyQuoteKey) {
      form.setFields([
        ...SHIPPING_TERMS_FIELDS.map((name) => ({ name, value: data?.[name] })),
        { name: 'expDeliveryDate', value: moment(data?.deliveryDate) },
        { name: 'expShipDate', value: moment(data?.shipDate) },
        {
          name: 'transportationGroup',
          value: transportationGroupData.find(
            (group) => group.value.toLowerCase() === data?.transportationGroup?.toLowerCase(),
          )?.id,
        },
      ]);
    }
  }, [copyQuoteKey, data, form]);

  return (
    <Form form={form} name="shippingTermsSearchForm">
      <Row gutter={30}>
        <Col className="w-20">
          <FormSchema.Field name="transportationGroup">
            {({ name, antdRule, isRequired, label }) => (
              <TransportationGroupSelect
                name={name}
                label={label}
                margin="m-0"
                editMode
                isRequired={isRequired}
                rules={[antdRule]}
              />
            )}
          </FormSchema.Field>
        </Col>
        <Col className="w-20">
          <FormSchema.Field name="expShipDate">
            {({ antdRule, name, isRequired, label }) => (
              <FormDatePicker
                name={name}
                label={label}
                margin="m-0"
                isRequired={isRequired}
                rules={[antdRule]}
                disabledTargetDate={Date.now()}
              />
            )}
          </FormSchema.Field>
        </Col>
        <Col className="w-20">
          <FormSchema.Field name="incoterms">
            {({ name, antdRule, isRequired, label }) => (
              <HalIncotermSelect
                label={label}
                margin="m-0"
                name={name}
                isRequired={isRequired}
                rules={[antdRule]}
                editMode
              />
            )}
          </FormSchema.Field>
        </Col>
        <Col className="w-20">
          <FormSchema.Field name="psl">
            {({ name, antdRule, isRequired, label }) => (
              <PSLSelect name={name} label={label} isRequired={isRequired} rules={[antdRule]} margin="m-0" />
            )}
          </FormSchema.Field>
        </Col>
        <Col className="w-20">
          <FormSchema.Field name="shipment">
            {({ name, antdRule, isRequired, label }) => (
              <FormInput
                name={name}
                label={label}
                margin="m-0"
                editMode
                isRequired={isRequired}
                rules={[antdRule]}
                validateTrigger="onChange"
              />
            )}
          </FormSchema.Field>
        </Col>
      </Row>

      <Row gutter={30} className="mt-2">
        <Col className="w-20">
          <FormSchema.Field name="urgency">
            {({ name, antdRule, isRequired, label }) => (
              <UrgencySelect name={name} label={label} isRequired={isRequired} rules={[antdRule]} />
            )}
          </FormSchema.Field>
        </Col>
        <Col className="w-20">
          <FormSchema.Field name="expDeliveryDate">
            {({ antdRule, name, isRequired, label }) => (
              <FormDatePicker
                name={name}
                label={label}
                margin="m-0"
                isRequired={isRequired}
                rules={[antdRule]}
                disabledTargetDate={shipDateValue || Date.now()}
              />
            )}
          </FormSchema.Field>
        </Col>
        <Col className="w-20">
          <FormSchema.Field name="incotermnamedPlace">
            {({ name, antdRule, isRequired, label }) => (
              <FormInput name={name} label={label} margin="m-0" editMode isRequired={isRequired} rules={[antdRule]} />
            )}
          </FormSchema.Field>
        </Col>
        <Col className="w-20">
          <FormSchema.Field name="ami">
            {({ name, antdRule, isRequired, label }) => (
              <FormInput
                name={name}
                label={label}
                margin="m-0"
                editMode
                isRequired={isRequired}
                rules={[antdRule]}
                validateTrigger="onChange"
              />
            )}
          </FormSchema.Field>
        </Col>
        <Col className="w-20">
          <FormSchema.Field name="po">
            {({ name, antdRule, isRequired, label }) => (
              <FormInput
                name={name}
                label={label}
                margin="m-0"
                editMode
                isRequired={isRequired}
                rules={[antdRule]}
                validateTrigger="onChange"
              />
            )}
          </FormSchema.Field>
        </Col>
        <FormSchema.Field name="trailerKey">
          {({ name, antdRule, isRequired, label }) => (
            <Col className="w-20">
              <EquipmentTypeSelect
                name={name}
                label={label}
                isRequired={isRequired}
                rules={[antdRule]}
                margin="m-0"
                editMode
                dataTestId="shipping-terms-equipment-type-selector"
              />
            </Col>
          )}
        </FormSchema.Field>
        <FormSchema.Field name="quoteTerms">
          {({ name, antdRule, isRequired, label }) => (
            <Col className="w-20">
              <ShipTermSelect
                name={name}
                label={label}
                isRequired={isRequired}
                rules={[antdRule]}
                margin="m-0"
                editMode
                dataTestId="shipping-terms-quote-terms-selector"
              />
            </Col>
          )}
        </FormSchema.Field>
      </Row>
    </Form>
  );
};

export default memo(ShippingTermsSearchForm);
